import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, ArrowRight, AlertCircle, FileText, Award, Calendar } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STAGES = ["Under Review", "Shortlisted", "Interview", "Technical Interview", "Managerial Interview", "HR Interview", "Selected", "Offer", "Rejected", "On Hold", "Withdrawn"];
const ROUNDS = ["Technical Interview","Managerial Interview","HR Interview","Final Interview","Custom Round"];

export default function AdminApplicationDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [openReq, setOpenReq] = useState(false);
  const [fields, setFields] = useState({});
  const [msg, setMsg] = useState("");
  const [newStage, setNewStage] = useState("");
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedForm, setSchedForm] = useState({ round_name:"Technical Interview", scheduled_at:"", duration_min:45, meeting_link:"", instructions:"", interviewer_name:"", interviewer_email:"" });

  const load = React.useCallback(()=>api.get(`/admin/applications/${id}`).then(r=>setD(r.data)),[id]);
  useEffect(()=>{ load(); },[load]);

  if (!d) return <AdminLayout><div className="text-slate-400">Loading...</div></AdminLayout>;
  const { application, candidate, job, assessment, missing } = d;

  const sendRequest = async () => {
    const selected = Object.entries(fields).filter(([_,v])=>v).map(([k])=>k);
    if (!selected.length) { toast.error("Select at least one field"); return; }
    try {
      await api.post(`/admin/applications/${application.id}/request-info`, { application_id: application.id, fields: selected, message: msg });
      toast.success("Request sent to candidate");
      setOpenReq(false); setFields({}); setMsg("");
    } catch(e){ toast.error("Failed to send request"); }
  };
  const updateStage = async () => {
    if (!newStage) return;
    try {
      await api.post(`/admin/applications/${application.id}/status`, { stage: newStage });
      toast.success(`Stage updated to ${newStage}`);
      setNewStage("");
      load();
    } catch(e){ toast.error("Update failed"); }
  };

  const scheduleInterview = async () => {
    if (!schedForm.scheduled_at) { toast.error("Please pick a date/time"); return; }
    try {
      await api.post("/admin/interviews", {
        ...schedForm,
        application_id: application.id,
        scheduled_at: new Date(schedForm.scheduled_at).toISOString(),
      });
      toast.success("Interview scheduled — candidate notified");
      setSchedOpen(false);
      setSchedForm({ round_name:"Technical Interview", scheduled_at:"", duration_min:45, meeting_link:"", instructions:"", interviewer_name:"", interviewer_email:"" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to schedule"); }
  };

  const p = candidate.profile || {};
  return (
    <AdminLayout>
      <div className="mb-6">
        <Link to="/admin/applications" className="text-sm text-[#F59E0B] hover:underline">← Back to applications</Link>
      </div>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F59E0B]">Application</div>
          <h1 className="font-display text-3xl font-bold text-white mt-1">{application.job_title}</h1>
          <p className="text-slate-400 mt-1 font-mono text-sm">{application.id}</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={newStage} onValueChange={setNewStage}>
            <SelectTrigger className="w-56 bg-slate-800 border-slate-700 text-white" data-testid="admin-stage-select"><SelectValue placeholder="Update stage..."/></SelectTrigger>
            <SelectContent>{STAGES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={updateStage} disabled={!newStage} className="bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" data-testid="admin-stage-update">Update</Button>
          <Button onClick={()=>setSchedOpen(true)} variant="outline" className="border-slate-700 bg-slate-900 text-white hover:bg-slate-800 hover:text-white" data-testid="schedule-interview-btn"><Calendar className="w-4 h-4 mr-2"/>Schedule Interview</Button>
          <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
            <DialogContent className="bg-slate-900 border-slate-800 text-white" data-testid="schedule-dialog">
              <DialogHeader><DialogTitle>Schedule Interview</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Round</Label>
                    <Select value={schedForm.round_name} onValueChange={v=>setSchedForm({...schedForm, round_name:v})}>
                      <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="sched-round"><SelectValue/></SelectTrigger>
                      <SelectContent>{ROUNDS.map(r=><SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Duration (min)</Label><Input type="number" value={schedForm.duration_min} onChange={e=>setSchedForm({...schedForm, duration_min: parseInt(e.target.value||"0",10)})} className="bg-slate-800 border-slate-700"/></div>
                </div>
                <div><Label>Date & Time</Label><Input type="datetime-local" value={schedForm.scheduled_at} onChange={e=>setSchedForm({...schedForm, scheduled_at:e.target.value})} className="bg-slate-800 border-slate-700" data-testid="sched-datetime"/></div>
                <div><Label>Meeting link (https)</Label><Input value={schedForm.meeting_link} onChange={e=>setSchedForm({...schedForm, meeting_link:e.target.value})} className="bg-slate-800 border-slate-700"/></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Interviewer name</Label><Input value={schedForm.interviewer_name} onChange={e=>setSchedForm({...schedForm, interviewer_name:e.target.value})} className="bg-slate-800 border-slate-700"/></div>
                  <div><Label>Interviewer email</Label><Input type="email" value={schedForm.interviewer_email} onChange={e=>setSchedForm({...schedForm, interviewer_email:e.target.value})} className="bg-slate-800 border-slate-700"/></div>
                </div>
                <div><Label>Instructions</Label><Textarea rows={2} value={schedForm.instructions} onChange={e=>setSchedForm({...schedForm, instructions:e.target.value})} className="bg-slate-800 border-slate-700"/></div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="border-slate-700 text-slate-200" onClick={()=>setSchedOpen(false)}>Cancel</Button>
                <Button onClick={scheduleInterview} className="bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" data-testid="sched-submit">Schedule</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={openReq} onOpenChange={setOpenReq}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-slate-700 bg-slate-900 text-white hover:bg-slate-800 hover:text-white" data-testid="admin-request-info-btn">
                <Mail className="w-4 h-4 mr-2"/>Request Missing Information
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 text-white">
              <DialogHeader><DialogTitle>Request missing information from candidate</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-sm text-slate-400">Select the fields the candidate needs to provide. They will receive an email and dashboard notification.</p>
                <div className="grid grid-cols-2 gap-3">
                  {["Resume","Phone","Location","Education","Experience","Skills","Documents","LinkedIn"].map(f=>(
                    <label key={f} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={!!fields[f]} onCheckedChange={v=>setFields({...fields,[f]:v})} data-testid={`req-field-${f}`}/>
                      {f}
                    </label>
                  ))}
                </div>
                <Textarea placeholder="Custom message (optional)" value={msg} onChange={e=>setMsg(e.target.value)} className="bg-slate-800 border-slate-700" data-testid="req-message"/>
              </div>
              <DialogFooter>
                <Button onClick={sendRequest} className="bg-[#F59E0B] text-[#0A2540] hover:bg-[#F59E0B]/90 font-semibold" data-testid="req-send"><Mail className="w-4 h-4 mr-2"/>Send Request</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {missing?.length>0 && (
        <Card className="bg-amber-500/10 border-amber-500/40 p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5"/>
          <div>
            <div className="font-semibold text-amber-300">Missing information detected</div>
            <div className="text-sm text-amber-200/80 mt-1">{missing.join(", ")}</div>
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="bg-slate-900 border-slate-800 p-6 lg:col-span-2">
          <h2 className="font-display font-semibold text-white text-lg">Candidate Profile</h2>
          <div className="grid md:grid-cols-2 gap-4 text-sm mt-4">
            <Field label="Name" value={candidate.full_name}/>
            <Field label="Email" value={candidate.email}/>
            <Field label="Phone" value={candidate.phone}/>
            <Field label="Location" value={p.location}/>
            <Field label="Current role" value={p.current_role}/>
            <Field label="Current company" value={p.current_company}/>
            <Field label="Experience" value={p.experience_years ? `${p.experience_years} years` : null}/>
            <Field label="Notice period" value={p.notice_period}/>
            <Field label="Expected CTC" value={p.expected_ctc}/>
            <Field label="LinkedIn" value={p.linkedin}/>
            <Field label="Portfolio" value={p.portfolio}/>
            <Field label="Resume" value={p.resume_url ? <a href={p.resume_url} className="text-[#F59E0B] underline" target="_blank" rel="noreferrer">Open resume</a> : null}/>
          </div>
          <div className="mt-6">
            <div className="text-xs uppercase tracking-wider text-slate-400">Skills</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(p.skills||[]).map(s=><Badge key={s} className="bg-slate-700 text-white border-none hover:bg-slate-700">{s}</Badge>)}
              {!(p.skills||[]).length && <span className="text-slate-500 text-sm">Not provided</span>}
            </div>
          </div>
          {(p.education||[]).length>0 && <div className="mt-6">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Education</div>
            <div className="space-y-2">
              {p.education.map((e,i)=><div key={i} className="text-sm text-slate-300"><span className="text-white font-medium">{e.degree}</span> · {e.institution} <span className="text-slate-500">({e.year})</span></div>)}
            </div>
          </div>}
          {(p.experience||[]).length>0 && <div className="mt-6">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Experience</div>
            <div className="space-y-2">
              {p.experience.map((x,i)=><div key={i} className="text-sm text-slate-300"><span className="text-white font-medium">{x.role}</span> at {x.company} <span className="text-slate-500">({x.dates})</span></div>)}
            </div>
          </div>}
        </Card>

        <div className="space-y-4">
          <Card className="bg-slate-900 border-slate-800 p-6">
            <h2 className="font-display font-semibold text-white text-lg flex items-center gap-2"><Award className="w-4 h-4 text-[#F59E0B]"/>Assessment</h2>
            {assessment ? (
              <div className="mt-3 text-sm space-y-2">
                <div className="text-slate-300"><span className="text-slate-500">Status:</span> {assessment.status}</div>
                {assessment.percentage != null && <div className="text-slate-300"><span className="text-slate-500">Score:</span> <span className="font-semibold text-white">{assessment.percentage}%</span></div>}
                <div className="text-slate-300"><span className="text-slate-500">Duration:</span> {assessment.duration_min}m</div>
                <div className="text-slate-300"><span className="text-slate-500">Skills:</span> {(assessment.skills||[]).join(", ")}</div>
                {assessment.skillwise?.length > 0 && <div className="mt-3">
                  <div className="text-xs text-slate-400 mb-1">Skill-wise</div>
                  {assessment.skillwise.map(s=><div key={s.skill} className="text-xs text-slate-300 flex justify-between border-b border-slate-800 py-1"><span>{s.skill}</span><span>{s.percentage}%</span></div>)}
                </div>}
              </div>
            ) : <p className="text-sm text-slate-500 mt-2">No assessment.</p>}
          </Card>

          <Card className="bg-slate-900 border-slate-800 p-6">
            <h2 className="font-display font-semibold text-white text-lg flex items-center gap-2"><FileText className="w-4 h-4 text-[#F59E0B]"/>Timeline</h2>
            <div className="mt-3 space-y-3">
              {(application.timeline||[]).map((t,i)=>(
                <div key={i} className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#F59E0B] mt-1.5"/>
                  <div>
                    <div className="text-sm text-white">{t.stage}</div>
                    <div className="text-[11px] text-slate-500">{new Date(t.at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
function Field({ label, value }) {
  return <div><div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div><div className="text-white mt-0.5 break-words">{value || <span className="text-slate-500">—</span>}</div></div>;
}
